import type {
  ApiErrorResponse,
  AuthGuestRequest,
  AuthLogoutResponse,
  AuthPayload,
  AuthTokenRequest,
  ChangeLobbyRosterRequest,
  CommunityRequestItem,
  CommunityRequestKind,
  CommunityRequestsResponse,
  CommunityVoteResponse,
  GameSnapshot,
  HealthResponse,
  LobbyTable,
  LobbyInviteResponse,
  LobbyMembershipResponse,
  LobbyVisibility,
  LobbyTableResponse,
  SetLobbyReadyRequest,
  StartLobbyTableResponse,
  LobbyTablesResponse,
  LinkedIdentity,
  User,
  UserCommunityVotesResponse,
  UserGamesResponse,
  UserGamesQuery,
  UserIdentitiesResponse,
  UserResponse,
  UserStats,
  UserStatsResponse,
} from '@everyturn/protocol';
import ky, { HTTPError, TimeoutError, type Options } from 'ky';
import { API_BASE_URL } from './api-config';
import { readSessionToken } from './browser-session';
import { diagnostics } from './diagnostics';

export type ApiUser = User;
export type ApiLobbyTable = LobbyTable;
export type ApiSession = AuthPayload['session'];
export type AuthMePayload = AuthPayload;
export type ApiStats = UserStats;
export type ApiGame = GameSnapshot;
export type ApiLinkedIdentity = LinkedIdentity;
export type ApiCommunityRequestItem = CommunityRequestItem;
export type ApiCommunityRequestKind = CommunityRequestKind;
export type ApiCommunityRequestsResponse = CommunityRequestsResponse;
export type ApiCommunityVoteResponse = CommunityVoteResponse;
export type ApiUserCommunityVotesResponse = UserCommunityVotesResponse;

export type ApiConnectivityState = 'ok' | 'degraded' | 'offline';
export type ApiConnectivityActivity = 'stable' | 'waiting-retry' | 'attempting-connect';

export interface ApiConnectivityStatus {
  state: ApiConnectivityState;
  since: number;
  lastError: string | null;
  activity: ApiConnectivityActivity;
  retryAttempt: number | null;
}

type ApiConnectivityListener = (status: ApiConnectivityStatus) => void;

const connectivityListeners = new Set<ApiConnectivityListener>();
let connectivityStatus: ApiConnectivityStatus = {
  state: typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'ok',
  since: Date.now(),
  lastError: null,
  activity: 'stable',
  retryAttempt: null,
};

function emitConnectivity(): void {
  const snapshot = getApiConnectivityStatus();
  for (const listener of connectivityListeners) {
    listener(snapshot);
  }
}

function updateConnectivity(
  nextState: ApiConnectivityState,
  lastError: string | null = null,
  options?: { activity?: ApiConnectivityActivity; retryAttempt?: number | null },
): void {
  const nextActivity = options?.activity ?? connectivityStatus.activity;
  const nextRetryAttempt = options?.retryAttempt ?? connectivityStatus.retryAttempt;
  const changedState = connectivityStatus.state !== nextState;
  const changedError = connectivityStatus.lastError !== lastError;
  const changedActivity = connectivityStatus.activity !== nextActivity;
  const changedRetryAttempt = connectivityStatus.retryAttempt !== nextRetryAttempt;
  if (!changedState && !changedError && !changedActivity && !changedRetryAttempt) {
    return;
  }
  connectivityStatus = {
    state: nextState,
    since: changedState ? Date.now() : connectivityStatus.since,
    lastError,
    activity: nextActivity,
    retryAttempt: nextRetryAttempt,
  };
  if (changedState) {
    if (nextState === 'ok') {
      diagnostics.report('api_restored');
      void diagnostics.flush();
    } else {
      diagnostics.report('api_unavailable');
    }
  }
  emitConnectivity();
}

function normalizeErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

function isLikelyOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

const globalScope = globalThis as typeof globalThis & { __bridgeApiConnectivityBound?: boolean };
if (typeof window !== 'undefined' && !globalScope.__bridgeApiConnectivityBound) {
  window.addEventListener('offline', () => {
    updateConnectivity('offline', 'Network offline', { activity: 'attempting-connect', retryAttempt: null });
  });
  window.addEventListener('online', () => {
    updateConnectivity('degraded', 'Network restored, reconnecting', {
      activity: 'attempting-connect',
      retryAttempt: null,
    });
  });
  globalScope.__bridgeApiConnectivityBound = true;
}

const apiClient = ky.create({
  prefixUrl: API_BASE_URL.replace(/\/+$/, ''),
  timeout: 7000,
  retry: {
    // Retry only idempotent reads to avoid accidental duplicate writes.
    limit: 2,
    methods: ['get'],
    statusCodes: [408, 413, 429, 500, 502, 503, 504],
  },
  hooks: {
    beforeRequest: [
      () => {
        if (connectivityStatus.state !== 'ok') {
          updateConnectivity(connectivityStatus.state, connectivityStatus.lastError, {
            activity: 'attempting-connect',
            retryAttempt: null,
          });
        }
      },
    ],
    beforeRetry: [
      ({ error, retryCount }) => {
        const detail = normalizeErrorMessage(error);
        updateConnectivity(isLikelyOffline() ? 'offline' : 'degraded', `Retry ${retryCount}: ${detail}`, {
          activity: 'waiting-retry',
          retryAttempt: retryCount,
        });
      },
    ],
    afterResponse: [
      (_request, _options, response) => {
        if (response.ok || (response.status >= 400 && response.status < 500)) {
          updateConnectivity('ok', null, { activity: 'stable', retryAttempt: null });
        } else if (response.status >= 500) {
          updateConnectivity('degraded', `Server ${response.status}`, { activity: 'stable', retryAttempt: null });
        }
      },
    ],
    beforeError: [
      (error) => {
        if (error instanceof HTTPError && error.response.status >= 400 && error.response.status < 500) return error;
        if (error instanceof TimeoutError) {
          updateConnectivity(isLikelyOffline() ? 'offline' : 'degraded', 'Request timeout', {
            activity: 'stable',
            retryAttempt: null,
          });
          return error;
        }
        updateConnectivity(isLikelyOffline() ? 'offline' : 'degraded', normalizeErrorMessage(error), {
          activity: 'stable',
          retryAttempt: null,
        });
        return error;
      },
    ],
  },
});

function buildRequestOptions(init?: RequestInit): Options {
  const method = (init?.method ?? 'GET').toUpperCase();
  const headers = new Headers(init?.headers ?? {});
  if (init?.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }

  return {
    method,
    body: init?.body ?? undefined,
    headers,
    signal: init?.signal ?? undefined,
  };
}

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  const normalizedPath = path.replace(/^\/+/, '');
  try {
    return await apiClient(normalizedPath, buildRequestOptions(init)).json<T>();
  } catch (error) {
    if (init?.signal?.aborted) throw init.signal.reason;
    if (error instanceof HTTPError) {
      const status = error.response.status;
      let detail = '';
      try {
        const payload = (await error.response.json()) as Partial<ApiErrorResponse>;
        if (payload.message) {
          detail = `: ${payload.message}`;
          if (payload.requestId) {
            detail += ` (requestId: ${payload.requestId})`;
          }
        }
      } catch {
        detail = '';
      }
      throw new ApiRequestError(status, `Request failed (${status})${detail}`);
    }

    updateConnectivity(isLikelyOffline() ? 'offline' : 'degraded', 'API request unavailable', {
      activity: 'stable',
      retryAttempt: null,
    });
    if (error instanceof TimeoutError) {
      throw new Error('Request timed out. Please try again.');
    }

    throw new Error(`Network request failed: ${normalizeErrorMessage(error)}`);
  }
}

export async function getHealth(): Promise<HealthResponse> {
  return requestJson('/health');
}

export async function revokeSession(token: string): Promise<void> {
  const response = await requestJson<AuthLogoutResponse>('/auth/logout', {
    method: 'POST',
    headers: authHeaders(token),
  });
  if (response?.revoked !== true) throw new Error('Server did not confirm session revocation.');
}

// Fire a health check on load so connectivity reflects actual server reachability
// (navigator.onLine only knows about the local network, not the API server).
if (typeof window !== 'undefined') {
  getHealth().catch(() => {});
}

export async function createGuestSession(displayName?: string): Promise<AuthMePayload> {
  const payload: AuthGuestRequest = displayName ? { displayName } : {};
  return requestJson<AuthMePayload>('/auth/guest', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getAuthMe(token: string): Promise<AuthMePayload> {
  return requestJson<AuthMePayload>('/auth/me', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getUserStats(token: string | null, userId: string, signal?: AbortSignal): Promise<ApiStats> {
  const payload = await requestJson<UserStatsResponse>(`/users/${userId}/stats`, {
    headers: authHeaders(token),
    signal,
  });
  return payload.stats;
}

export async function getUserGames(
  token: string | null,
  userId: string,
  query: UserGamesQuery,
  signal?: AbortSignal,
): Promise<UserGamesResponse> {
  const search = new URLSearchParams();
  if (query.limit !== undefined) search.set('limit', String(query.limit));
  if (query.cursor !== undefined) search.set('cursor', query.cursor);
  return requestJson<UserGamesResponse>(
    `/users/${encodeURIComponent(userId)}/games${search.size ? `?${search}` : ''}`,
    {
      headers: authHeaders(token),
      signal,
    },
  );
}

export async function getUserIdentities(
  token: string | null,
  userId: string,
  signal?: AbortSignal,
): Promise<ApiLinkedIdentity[]> {
  const payload = await requestJson<UserIdentitiesResponse>(`/users/${userId}/identities`, {
    headers: authHeaders(token),
    signal,
  });
  return payload.identities;
}

export async function regenerateUsername(token: string, userId: string, signal: AbortSignal): Promise<ApiUser> {
  const payload = await requestJson<UserResponse>(`/users/${userId}/username/regenerate`, {
    method: 'POST',
    headers: authHeaders(token),
    signal,
  });
  return payload.user;
}

export function getApiBaseUrl(): string {
  return API_BASE_URL;
}

export async function exchangeFirebaseToken(idToken: string): Promise<AuthMePayload> {
  const body: AuthTokenRequest = { provider: 'firebase', idToken };
  return requestJson<AuthMePayload>('/auth/token', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function linkProvider(idToken: string): Promise<ApiLinkedIdentity[]> {
  const token = readSessionToken();
  const body: AuthTokenRequest = { provider: 'firebase', idToken };
  const payload = await requestJson<UserIdentitiesResponse>('/auth/link', {
    method: 'POST',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  return payload.identities;
}

export async function unlinkProvider(userId: string, provider: string): Promise<ApiLinkedIdentity[]> {
  const token = readSessionToken();
  const payload = await requestJson<UserIdentitiesResponse>(`/users/${userId}/identities/${provider}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return payload.identities;
}

export function getGoogleAuthStartUrl(): string {
  return `${API_BASE_URL}/auth/google/start`;
}

export function getApiConnectivityStatus(): ApiConnectivityStatus {
  return { ...connectivityStatus };
}

export function subscribeApiConnectivity(listener: ApiConnectivityListener): () => void {
  connectivityListeners.add(listener);
  listener(getApiConnectivityStatus());
  return () => {
    connectivityListeners.delete(listener);
  };
}

/* ── Lobby ── */

function authHeaders(token: string | null = readSessionToken()): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function listLobbyTables(token: string | null): Promise<ApiLobbyTable[]> {
  const payload = await requestJson<LobbyTablesResponse>('/lobby/tables', { headers: authHeaders(token) });
  return payload.tables;
}

export async function getLobbyMembership(token: string | null): Promise<ApiLobbyTable | null> {
  return (await requestJson<LobbyMembershipResponse>('/lobby/membership', { headers: authHeaders(token) })).table;
}

export async function getLobbyTable(
  token: string | null,
  tableId: string,
  inviteToken?: string,
  signal?: AbortSignal,
): Promise<ApiLobbyTable> {
  return (
    await requestJson<LobbyTableResponse>(`/lobby/tables/${encodeURIComponent(tableId)}`, {
      headers: { ...authHeaders(token), ...(inviteToken ? { 'x-table-invite': inviteToken } : {}) },
      signal,
    })
  ).table;
}

export async function manageLobbyInvite(
  token: string | null,
  tableId: string,
  method: 'GET' | 'POST' | 'DELETE',
): Promise<string | null> {
  return (
    await requestJson<LobbyInviteResponse>(`/lobby/tables/${encodeURIComponent(tableId)}/invite`, {
      method,
      headers: authHeaders(token),
    })
  ).inviteToken;
}

export async function createLobbyTable(
  token: string | null,
  gameType: string,
  maxSeats: number,
  visibility: LobbyVisibility = 'public',
): Promise<ApiLobbyTable> {
  const payload = await requestJson<LobbyTableResponse>('/lobby/tables', {
    method: 'POST',
    body: JSON.stringify({ gameType, maxSeats, visibility }),
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
  });
  return payload.table;
}

export async function joinLobbyTable(
  token: string | null,
  tableId: string,
  inviteToken?: string,
): Promise<ApiLobbyTable> {
  const payload = await requestJson<LobbyTableResponse>(`/lobby/tables/${encodeURIComponent(tableId)}/join`, {
    method: 'POST',
    headers: { ...authHeaders(token), ...(inviteToken ? { 'x-table-invite': inviteToken } : {}) },
  });
  return payload.table;
}

export async function leaveLobbyTable(token: string | null, tableId: string): Promise<ApiLobbyTable> {
  const payload = await requestJson<LobbyTableResponse>(`/lobby/tables/${encodeURIComponent(tableId)}/leave`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return payload.table;
}

export async function cancelLobbyTable(token: string | null, tableId: string): Promise<ApiLobbyTable> {
  const payload = await requestJson<LobbyTableResponse>(`/lobby/tables/${encodeURIComponent(tableId)}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  return payload.table;
}

export async function setLobbyReady(
  token: string | null,
  tableId: string,
  input: SetLobbyReadyRequest,
): Promise<ApiLobbyTable> {
  const payload = await requestJson<LobbyTableResponse>(`/lobby/tables/${encodeURIComponent(tableId)}/ready`, {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return payload.table;
}

export async function changeLobbyRoster(
  token: string | null,
  tableId: string,
  input: ChangeLobbyRosterRequest,
): Promise<ApiLobbyTable> {
  return (
    await requestJson<LobbyTableResponse>(`/lobby/tables/${encodeURIComponent(tableId)}/roster`, {
      method: 'POST',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
  ).table;
}

export async function touchLobbyTable(token: string | null, tableId: string): Promise<ApiLobbyTable> {
  return (
    await requestJson<LobbyTableResponse>(`/lobby/tables/${encodeURIComponent(tableId)}/presence`, {
      method: 'POST',
      headers: authHeaders(token),
    })
  ).table;
}

export async function requestLobbyRematch(
  token: string | null,
  tableId: string,
  signal?: AbortSignal,
): Promise<ApiLobbyTable> {
  return (
    await requestJson<LobbyTableResponse>(`/lobby/tables/${encodeURIComponent(tableId)}/rematch`, {
      method: 'POST',
      headers: authHeaders(token),
      signal,
    })
  ).table;
}

export async function startLobbyTable(
  token: string | null,
  tableId: string,
  rosterRevision: number,
): Promise<StartLobbyTableResponse> {
  return requestJson<StartLobbyTableResponse>(`/lobby/tables/${encodeURIComponent(tableId)}/start`, {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ rosterRevision }),
  });
}

export function getWsBaseUrl(): string {
  return API_BASE_URL.replace(/^http/, 'ws');
}

/* ── Community Feature & Game Requests ── */

export async function listCommunityRequests(): Promise<ApiCommunityRequestsResponse> {
  return requestJson<ApiCommunityRequestsResponse>('/community/requests', {
    headers: authHeaders(),
  });
}

export async function voteCommunityRequest(issueNumber: number): Promise<ApiCommunityVoteResponse> {
  return requestJson<ApiCommunityVoteResponse>(`/community/requests/${issueNumber}/vote`, {
    method: 'POST',
    headers: authHeaders(),
  });
}

export async function unvoteCommunityRequest(issueNumber: number): Promise<ApiCommunityVoteResponse> {
  return requestJson<ApiCommunityVoteResponse>(`/community/requests/${issueNumber}/vote`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
}

export async function getUserCommunityVotes(userId: string): Promise<ApiUserCommunityVotesResponse> {
  return requestJson<ApiUserCommunityVotesResponse>(`/users/${userId}/votes`, {
    headers: authHeaders(),
  });
}

export async function signInDebugTestUser(input?: {
  displayName?: string;
  email?: string;
  provider?: 'google' | 'apple' | 'facebook';
}): Promise<AuthMePayload> {
  return requestJson<AuthMePayload>('/debug/auth/test-user', {
    method: 'POST',
    body: JSON.stringify(input ?? {}),
    headers: { 'Content-Type': 'application/json' },
  });
}
